
import React, { useState, useEffect } from 'react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { CreditCard, CheckCircle, X, Filter, DollarSign } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface TransactionFilterMenuProps {
  onFilterChange: (filterType: string, value: string | null) => void;
  activeFilters: Record<string, string | null>;
  onClearFilters: () => void;
}

const TransactionFilterMenu: React.FC<TransactionFilterMenuProps> = ({ 
  onFilterChange, 
  activeFilters,
  onClearFilters
}) => {
  const [minAmount, setMinAmount] = useState<string>('');
  
  // Sync local state with active filters
  useEffect(() => {
    if (activeFilters.minAmount) {
      setMinAmount(activeFilters.minAmount);
    } else {
      setMinAmount('');
    }
  }, [activeFilters.minAmount]);
  
  const handleAmountFilterChange = () => {
    if (minAmount && !isNaN(parseFloat(minAmount))) {
      // Parse the input as a float - we're dealing with dollars here
      const numAmount = parseFloat(parseFloat(minAmount).toFixed(2));
      console.log('[Filter Menu] Setting min amount filter:', numAmount.toFixed(2));
      onFilterChange('minAmount', numAmount.toString());
    } else if (minAmount === '') {
      // Clear the filter if the input is empty
      onFilterChange('minAmount', null);
    }
  };
  
  const hasActiveFilters = Object.values(activeFilters).some(value => value !== null);
  
  return (
    <div className="flex items-center gap-2">
      {/* Payment Method Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className={`flex items-center gap-2 ${activeFilters.paymentMethod ? 'border-primary bg-primary/10 text-primary' : ''}`}
          >
            <CreditCard className="h-4 w-4" />
            Payment Method
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuLabel>Filter by payment method</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={activeFilters.paymentMethod === 'card'}
            onCheckedChange={() => {
              console.log('[Filter Menu] Toggling card filter');
              onFilterChange('paymentMethod', activeFilters.paymentMethod === 'card' ? null : 'card');
            }}
          >
            Credit Card
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={activeFilters.paymentMethod === 'us_bank_account'}
            onCheckedChange={() => {
              console.log('[Filter Menu] Toggling bank filter');
              onFilterChange('paymentMethod', activeFilters.paymentMethod === 'us_bank_account' ? null : 'us_bank_account');
            }}
          >
            Bank Transfer (ACH)
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* KeepMore Status Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className={`flex items-center gap-2 ${activeFilters.keepMoreStatus ? 'border-primary bg-primary/10 text-primary' : ''}`}
          >
            <CheckCircle className="h-4 w-4" />
            Processor
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          <DropdownMenuLabel>Filter by processor</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem
            checked={activeFilters.keepMoreStatus === 'true'}
            onCheckedChange={() => {
              console.log('[Filter Menu] Toggling KeepMore filter');
              onFilterChange('keepMoreStatus', activeFilters.keepMoreStatus === 'true' ? null : 'true');
            }}
          >
            KeepMore
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={activeFilters.keepMoreStatus === 'false'}
            onCheckedChange={() => {
              console.log('[Filter Menu] Toggling Direct Stripe filter');
              onFilterChange('keepMoreStatus', activeFilters.keepMoreStatus === 'false' ? null : 'false');
            }}
          >
            Direct Stripe
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Amount Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className={`flex items-center gap-2 ${activeFilters.minAmount ? 'border-primary bg-primary/10 text-primary' : ''}`}
          >
            <DollarSign className="h-4 w-4" />
            Amount
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-60 p-3">
          <DropdownMenuLabel>Minimum amount</DropdownMenuLabel>
          <div className="flex items-center gap-2 mt-2 mb-1">
            <span className="text-sm">$</span>
            <Input
              type="number"
              min="0"
              step="0.01" 
              placeholder="Minimum amount"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="h-8"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAmountFilterChange();
                }
              }}
            />
            <Button size="sm" onClick={handleAmountFilterChange}>Apply</Button>
          </div>
          {activeFilters.minAmount && (
            <div className="text-xs text-muted-foreground mt-1">
              Currently: ${parseFloat(activeFilters.minAmount).toFixed(2)}+
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Clear All Filters Button */}
      {hasActiveFilters && (
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => {
            console.log('[Filter Menu] Clearing all filters');
            onClearFilters();
          }}
          className="flex items-center gap-1 text-destructive hover:text-destructive"
        >
          <X className="h-4 w-4" />
          Clear filters
        </Button>
      )}
    </div>
  );
};

export default TransactionFilterMenu;
